/**
 * Stable per-user identity for multi-device sync.
 *
 * On first launch we mint a `device:<UUID>` id and stash it in localStorage.
 * Every API request includes it as the `X-User-Id` header. After the user
 * verifies their email, the backend returns a `new_user_id` of the form
 * `email:<address>` — we swap it in here so data follows the user across
 * devices that verify the same email.
 */

const STORAGE_KEY = "uptop-user-id";

function genDeviceId() {
  // Prefer crypto.randomUUID when available (modern browsers + native shells)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `device:${crypto.randomUUID()}`;
  }
  // Fallback: random hex
  const rand = Array.from({ length: 4 }, () =>
    Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, "0")
  ).join("-");
  return `device:${rand}`;
}

export function getUserId() {
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id || !id.includes(":")) {
      id = genDeviceId();
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch (e) {
    return genDeviceId();
  }
}

export function setUserId(id) {
  try { localStorage.setItem(STORAGE_KEY, id); } catch (e) { /* ignore */ }
}

export function clearUserId() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
}

/**
 * Wrapper around fetch() that automatically adds the X-User-Id header.
 * Components can either use this OR rely on the axios interceptor installed
 * in installAxiosInterceptor().
 */
export async function apiFetch(input, init) {
  const headers = new Headers((init && init.headers) || {});
  headers.set("X-User-Id", getUserId());
  return fetch(input, { ...(init || {}), headers });
}

/**
 * Install a global axios interceptor that injects X-User-Id on every
 * outgoing request. Call once during app bootstrap.
 */
export function installAxiosInterceptor(axiosInstance) {
  if (!axiosInstance || axiosInstance.__uptopInterceptorInstalled) return;
  axiosInstance.interceptors.request.use((config) => {
    config.headers = config.headers || {};
    config.headers["X-User-Id"] = getUserId();
    return config;
  });
  axiosInstance.__uptopInterceptorInstalled = true;
}
